import { memo, useState } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Tag } from '@carbon/react';
import { DataBase, Document, TextShortParagraph } from '@carbon/icons-react';
import type { ExplorationGraphNode, NodeLayer } from '@snxt/snxt-onto-share';

export interface SourceNodeData
  extends ExplorationGraphNode,
    Record<string, unknown> {}

const LAYER_STYLES: Record<
  string,
  { iconColor: string; borderColor: string; Icon: typeof DataBase; label: string }
> = {
  kb: {
    iconColor: '#6929c4',
    borderColor: 'var(--cds-support-purple-60, #6929c4)',
    Icon: DataBase,
    label: 'KB',
  },
  ko: {
    iconColor: '#1192e8',
    borderColor: 'var(--cds-support-cyan-50, #1192e8)',
    Icon: Document,
    label: 'KO',
  },
  ku: {
    iconColor: '#198038',
    borderColor: 'var(--cds-support-green-60, #198038)',
    Icon: TextShortParagraph,
    label: 'KU',
  },
};

/**
 * Lightweight source-graph node for kb / ko / ku layers.
 * Used in KB-view and Concept-view modes.
 *
 *  kb  → purple, DataBase icon
 *  ko  → cyan, Document icon
 *  ku  → green, TextShortParagraph icon, shows content snippet
 */
function SourceNodeInner({ data, selected }: NodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const d = data as SourceNodeData;
  const layer = (d.layer ?? 'ku') as NodeLayer;
  const style = LAYER_STYLES[layer] ?? LAYER_STYLES.ku;
  const Icon = style.Icon;

  const boxShadow = selected
    ? '0 0 0 2px var(--cds-focus)'
    : isHovered
      ? `0 2px 8px rgba(0,0,0,0.18)`
      : '0 1px 4px rgba(0,0,0,0.10)';

  return (
    <>
      <Handle id="target-top"    type="target" position={Position.Top}    style={{ opacity: 0 }} />
      <Handle id="target-bottom" type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle id="target-left"   type="target" position={Position.Left}   style={{ opacity: 0 }} />
      <Handle id="target-right"  type="target" position={Position.Right}  style={{ opacity: 0 }} />
      <Handle id="source-top"    type="source" position={Position.Top}    style={{ opacity: 0 }} />
      <Handle id="source-bottom" type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle id="source-left"   type="source" position={Position.Left}   style={{ opacity: 0 }} />
      <Handle id="source-right"  type="source" position={Position.Right}  style={{ opacity: 0 }} />

      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          minWidth: '130px',
          maxWidth: '220px',
          padding: '0.5rem 0.625rem',
          backgroundColor: 'var(--cds-layer-02)',
          border: `${selected ? 2 : 1}px solid ${selected ? 'var(--cds-focus)' : style.borderColor}`,
          borderLeft: `3px solid ${style.iconColor}`,
          borderRadius: '4px',
          boxShadow,
          transition: 'all 0.15s ease',
          cursor: 'default',
          opacity: 0.92,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.375rem' }}>
          {/* Icon */}
          <Icon
            size={14}
            style={{ fill: style.iconColor, flexShrink: 0, marginTop: '1px' }}
          />

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Layer badge + label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.25rem' }}>
              <Tag size="sm" type="outline" style={{ fontSize: '0.625rem', padding: '0 0.25rem' }}>
                {style.label}
              </Tag>
              <span
                style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: 'var(--cds-text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {d.label}
              </span>
            </div>

            {/* KU content snippet */}
            {layer === 'ku' && d.source_content && (
              <p
                style={{
                  fontSize: '0.625rem',
                  color: 'var(--cds-text-secondary)',
                  lineHeight: 1.35,
                  margin: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {d.source_content}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export const SourceNode = memo(SourceNodeInner);

// Made with Bob
